/**
 * Inquiry-type taxonomy schema for the client SDK.
 *
 * Extends the SDK-agnostic base from `../../documents/emailAgent/inquiryType`
 * with `Timestamp`-transforming timestamp fields, and exposes typed refs used
 * by the admin dashboard's "Inquiry types" section to list, add, edit, and
 * deprecate types.
 */
import type {
  CollectionReference,
  DocumentReference,
  Firestore,
} from 'firebase/firestore';
import {z} from 'zod';
import {collection, doc, getFirestore} from 'firebase/firestore';

import {
  InquiryTypeDocumentSchema,
  getInquiryTypeDocumentPath,
  getInquiryTypesCollectionPath,
} from '../../documents/emailAgent/inquiryType';
import {ClientTimestampSchema, createConverter} from '../sdk';

/**
 * Client-SDK inquiry-type schema; timestamps become `Timestamp` instances.
 *
 * Both stamps are nullable here, unlike the always-set base
 * {@link InquiryTypeDocumentSchema}, because the admin dashboard is the
 * *creator* of these documents as well as their editor — seeding, adding,
 * editing, and deprecating are all client writes, and each stamps with
 * `serverTimestamp()`. The Firestore client SDK resolves a still-pending
 * sentinel to `null` in the transient local snapshot that fires before the
 * server ack, so either stamp can arrive null. The converter below validates
 * on read with a throwing `parse`, so a non-nullable stamp would turn that
 * latency-compensated snapshot into a `ZodError` while the dashboard renders.
 */
export const InquiryTypeSchema = InquiryTypeDocumentSchema.extend({
  createdAt: ClientTimestampSchema.nullable(),
  updatedAt: ClientTimestampSchema.nullable(),
}).loose();

/** Inferred type for {@link InquiryTypeSchema}. */
export type InquiryType = z.infer<typeof InquiryTypeSchema>;

const inquiryTypeConverter = createConverter({
  schema: InquiryTypeSchema,
  options: {validateOnRead: true, validateOnWrite: false},
});

/**
 * Typed ref to the inquiry-types collection. Apply `query`/`orderBy` at the
 * call site.
 *
 * @param args.firestore - Firestore instance; defaults to `getFirestore()`
 *   when omitted.
 * @returns Converter-bound collection ref for `reports/_agent/inquiryTypes`.
 * @throws Never at call time — building the ref is pure. The bound converter
 *   validates on read, so reading a malformed document surfaces a `ZodError`
 *   from the snapshot, not from this function.
 */
export function getInquiryTypesCollectionRef({
  firestore = getFirestore(),
}: {
  firestore?: Firestore;
} = {}): CollectionReference<InquiryType> {
  return collection(firestore, getInquiryTypesCollectionPath()).withConverter(
    inquiryTypeConverter,
  );
}

/**
 * Typed ref to a single inquiry-type document.
 *
 * @param args.typeId - The inquiry-type slug (document id).
 * @param args.firestore - Firestore instance; defaults to `getFirestore()`
 *   when omitted.
 * @returns Converter-bound doc ref for `reports/_agent/inquiryTypes/{typeId}`.
 * @throws Never at call time — building the ref is pure. The bound converter
 *   validates on read, so reading a malformed document surfaces a `ZodError`
 *   from the snapshot, not from this function.
 */
export function getInquiryTypeDocRef({
  typeId,
  firestore = getFirestore(),
}: {
  typeId: string;
  firestore?: Firestore;
}): DocumentReference<InquiryType> {
  return doc(firestore, getInquiryTypeDocumentPath({typeId})).withConverter(
    inquiryTypeConverter,
  );
}
